import { CuratedTopicsFilter } from '@ms/yammer-graphql';
import { Reducer } from 'redux';

import { TopicCurateAction } from '../../domains/topics/curateActions.types';
import { RemoveCuratedTopicFulfilledAction } from '../../domains/topics/removeCuratedActions.types';
import { RemoveTopicFulfilledProps } from '../../domains/topics/removeTopicActions';
import { RemoveTopicFulfilledAction } from '../../domains/topics/removeTopicActions.types';
import { TopicUncurateAction } from '../../domains/topics/uncurateActions.types';
import { TopicsCuratedLoadAction, TopicsCuratedLoadMoreAction } from '../../domains/topicsCurated/actions.types';
import { maxCuratedTopicsCountToShow } from '../../domains/topicsCurated/selectors';
import { Topic } from '../types';

import { TopicsCurated, TopicsCuratedEdge } from './types';

const loadTopicsCurated = (
  state: TopicsCurated,
  topicsCuratedEdge: TopicsCuratedEdge,
  topicsFilter: CuratedTopicsFilter
) => ({
  ...state,
  [topicsFilter]: topicsCuratedEdge,
});

const loadMoreTopicsCurated = (
  state: TopicsCurated,
  topicsCuratedEdge: TopicsCuratedEdge,
  topicsFilter: CuratedTopicsFilter
) => {
  const currentTopicIds = state[topicsFilter]?.topicIds ?? [];
  const topicIds = [...currentTopicIds, ...topicsCuratedEdge.topicIds];

  return { ...state, [topicsFilter]: { ...topicsCuratedEdge, topicIds } };
};

const getUpdatedCount = (count: number | undefined) => {
  if (!count) {
    return undefined;
  }

  if (count > maxCuratedTopicsCountToShow) {
    return maxCuratedTopicsCountToShow;
  }

  return count - 1;
};

const removeFromCurated = (state: TopicsCurated, topicId: Topic['id']) => {
  const proposedTopics = state['CURATED_PROPOSED'];

  if (!proposedTopics) {
    return state;
  }

  const newState = {
    ...state,
    ['CURATED_PROPOSED']: {
      ...proposedTopics,
      topicIds: proposedTopics.topicIds.filter((id: Topic['id']) => id !== topicId),
      topicsCount: proposedTopics.topicIds.includes(topicId)
        ? getUpdatedCount(proposedTopics.topicsCount)
        : proposedTopics.topicsCount,
    },
  };

  return newState;
};

const removeFromDefault = (state: TopicsCurated, topicId: Topic['id']) => {
  const defaultTopics = state['DEFAULT'];

  if (!defaultTopics) {
    return state;
  }

  const newState = {
    ...state,
    ['DEFAULT']: {
      ...defaultTopics,
      topicIds: defaultTopics.topicIds.filter((id: Topic['id']) => id !== topicId),
      topicsCount: defaultTopics.topicIds.includes(topicId)
        ? getUpdatedCount(defaultTopics.topicsCount)
        : defaultTopics.topicsCount,
    },
  };

  return newState;
};

const removeTopic = (state: TopicsCurated, topicIds: RemoveTopicFulfilledProps) => {
  const curatedTopicId = topicIds?.curatedTopicId;

  return curatedTopicId ? removeFromAllFilterBuckets(state, curatedTopicId) : state;
};

const removeFromAllFilterBuckets = (state: TopicsCurated, topicId: Topic['id']) => {
  const stateWithTopicRemovedFromDefault = removeFromDefault(state, topicId);

  const approvedTopics = state['CURATED_APPROVED'];
  const proposedTopics = state['CURATED_PROPOSED'];

  if (approvedTopics?.topicIds.includes(topicId)) {
    return {
      ...stateWithTopicRemovedFromDefault,
      ['CURATED_APPROVED']: {
        ...approvedTopics,
        topicIds: approvedTopics.topicIds.filter((id: Topic['id']) => id !== topicId),
        topicsCount: getUpdatedCount(approvedTopics.topicsCount),
      },
    };
  }

  if (proposedTopics?.topicIds.includes(topicId)) {
    return removeFromCurated(stateWithTopicRemovedFromDefault, topicId);
  }

  return stateWithTopicRemovedFromDefault;
};

const initialState: TopicsCurated = {};

export type TopicsCuratedAction =
  | RemoveCuratedTopicFulfilledAction
  | TopicsCuratedLoadAction
  | TopicsCuratedLoadMoreAction
  | TopicUncurateAction
  | TopicCurateAction
  | RemoveTopicFulfilledAction;

const reducer: Reducer<TopicsCurated, TopicsCuratedAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'TOPICS_CURATED.LOAD_FULFILLED':
      return loadTopicsCurated(state, action.payload.entities.topicsCuratedEdge, action.payload.topicsFilter);
    case 'TOPICS_CURATED.LOAD_MORE_FULFILLED':
      return loadMoreTopicsCurated(state, action.payload.entities.topicsCuratedEdge, action.payload.topicsFilter);
    case 'TOPIC.UNCURATE_FULFILLED':
      return removeFromCurated(state, action.payload.topicId);
    case 'TOPIC.CURATE_FULFILLED':
      return removeFromCurated(state, action.payload.topicId);
    case 'CURATED_TOPIC.REMOVE_FULFILLED':
      return removeFromAllFilterBuckets(state, action.payload.topicId);
    case 'TOPIC.REMOVE_FULFILLED':
      return removeTopic(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
